import React from 'react';
import Card from '@components/framework/card/Card.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import SvgDelete from '@icons/delete-trash.svg?react';
import SvgShuffle from '@icons/shuffle.svg?react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { MergeConflict, MergeSummary } from '@app/pages/expedition/create/mappers/mergeToForm.ts';
import { LocationContactField, MergedRoute } from '@app/pages/expedition/create/mergeRules.ts';
import { MergeSource } from '@app/pages/expedition/create/mergeEligibility.ts';
import MergeSummaryList from '@app/pages/expedition/create/components/MergeSummaryList.tsx';

interface ComponentProps {
  sources: MergeSource[];
  conflicts: MergeConflict[];
  keptApart: MergedRoute['keptApart'];
  contactConflicts: MergedRoute['contactConflicts'];
  summary: MergeSummary;
  /** Drops one entity from the merge; below two the form stops being a merge at all. */
  onRemoveSource: (sourceId: string) => void;
  canRemoveSource: boolean;
}

const CONFLICT_LABELS: Record<MergeConflict['field'], [string, string]> = {
  expeditionType: ['CreateExpedition.mergeFieldExpeditionType', 'Shipment type'],
  truckType: ['CreateExpedition.mergeFieldTruckType', 'Truck type'],
  cargoType: ['CreateExpedition.mergeFieldCargoType', 'Cargo type'],
  transportationType: ['CreateExpedition.mergeFieldTransportationType', 'Transportation type'],
  incoterms: ['CreateExpedition.mergeFieldIncoterms', 'Incoterms'],
  responsiblePerson: ['CreateExpedition.mergeFieldResponsiblePerson', 'Responsible person'],
  disponentPerson: ['CreateExpedition.mergeFieldDisponentPerson', 'Disponent'],
  pairingSymbol: ['CreateExpedition.mergeFieldPairingSymbol', 'Pairing symbol'],
  invoiceState: ['CreateExpedition.mergeFieldInvoiceState', 'Invoice state'],
  rebilling: ['CreateExpedition.mergeFieldRebilling', 'Rebilling'],
  internalNote: ['CreateExpedition.mergeFieldInternalNote', 'Internal note'],
  distribution: ['CreateExpedition.mergeFieldDistribution', 'Carrier selection'],
  polandSent: ['CreateExpedition.mergeFieldPolandSent', 'SENT'],
  huEkaerNumber: ['CreateExpedition.mergeFieldEkaer', 'Ekaer number'],
  roUitNumber: ['CreateExpedition.mergeFieldUit', 'UIT number'],
  utnCode: ['CreateExpedition.mergeFieldUtn', 'UTN code'],
  internalInfoCodes: ['CreateExpedition.mergeFieldInternalInfoCodes', 'Internal info code lists'],
};

const CONTACT_LABELS: Record<LocationContactField, [string, string]> = {
  contactPerson: ['CreateExpedition.mergeFieldContactPerson', 'Contact person'],
  contactPhone: ['CreateExpedition.mergeFieldContactPhone', 'Contact phone'],
  contactEmail: ['CreateExpedition.mergeFieldContactEmail', 'Contact email'],
};

/**
 * The shipments this form will replace, and everything the merge could not decide on its own.
 *
 * Conflicting fields are listed rather than guessed at: the form left them empty, and the user has
 * to say which value the merged shipment should carry.
 */
const MergeInfoPanel: React.FC<ComponentProps> = ({ sources, conflicts, keptApart, contactConflicts, summary, onRemoveSource, canRemoveSource }) => {
  const { tr } = useTranslationLgs();

  return (
    <Card>
      <div className={'flex flex-col gap-4'}>
        <COHeadline variant={'h4'} title={tr('CreateExpedition.mergeHeading', 'Merging shipments')} icon={<SvgShuffle />} />

        <span className={'text-content-secondary'}>
          {tr('CreateExpedition.mergeExplanation', 'These shipments will be closed and replaced by the one you create here.')}
        </span>

        {sources.some(source => source.kind === 'supplierNotification') && (
          <span className={'text-content-secondary'}>
            {tr(
              'CreateExpedition.mergeExplanationSupplierNotifications',
              'A supplier notification that already has a shipment of its own has that shipment closed as well, even though you did not select it.',
            )}
          </span>
        )}

        <div className={'flex flex-col gap-2'} data-testid={'mergeSources'}>
          {sources.map(source => (
            <div
              key={source.id}
              className={'flex flex-row items-center justify-between gap-4 rounded border border-solid border-content-quaternary bg-base-tertiary px-3 py-2'}
              data-testid={`mergeSource-${source.id}`}
            >
              <span className={'flex flex-row items-center gap-2'}>
                <span className={'text-content-secondary'}>{source.applicationId || source.id}</span>
                {source.kind === 'supplierNotification' && (
                  <span className={'rounded bg-content-brandMedium px-2 py-1 text-[12px] text-content-secondary'}>
                    {tr('CreateExpedition.mergeSourceSupplierNotification', 'Supplier notification')}
                  </span>
                )}
              </span>
              {canRemoveSource && (
                <RingilButton
                  mode={'table'}
                  size={'small'}
                  icon={<SvgDelete />}
                  tooltip={tr('CreateExpedition.mergeRemoveSourceTooltip', 'Do not merge this one')}
                  onClick={() => onRemoveSource(source.id)}
                  dataTestId={`mergeSource-${source.id}-remove`}
                />
              )}
            </div>
          ))}
        </div>

        <COInlineMessage variant={'info'} dataTestId={'mergeSummary'}>
          <div className={'flex flex-col gap-1'}>
            <strong>{tr('CreateExpedition.mergeSummaryHeading', 'What this merge does')}</strong>
            <MergeSummaryList summary={summary} />
          </div>
        </COInlineMessage>

        {conflicts.length > 0 && (
          <COInlineMessage variant={'alert'} dataTestId={'mergeConflicts'}>
            <div className={'flex flex-col gap-1'}>
              <strong>{tr('CreateExpedition.mergeConflictsHeading', 'These fields differed between the shipments and were left empty')}</strong>
              <ul className={'m-0 flex list-disc flex-col gap-1 pl-4'}>
                {conflicts.map(conflict => {
                  const [key, fallback] = CONFLICT_LABELS[conflict.field];
                  return (
                    <li key={conflict.field}>
                      {tr(key, fallback)}: {conflict.values.join(' / ')}
                    </li>
                  );
                })}
              </ul>
            </div>
          </COInlineMessage>
        )}

        {contactConflicts.length > 0 && (
          <COInlineMessage variant={'alert'} dataTestId={'mergeContactConflicts'}>
            <div className={'flex flex-col gap-1'}>
              <strong>{tr('CreateExpedition.mergeContactConflictsHeading', 'These stops were combined, but their contact details differed and were left empty')}</strong>
              <ul className={'m-0 flex list-disc flex-col gap-1 pl-4'}>
                {contactConflicts.map(conflict => {
                  const [key, fallback] = CONTACT_LABELS[conflict.field];
                  return (
                    <li key={`${conflict.locationName}-${conflict.field}`}>
                      {conflict.locationName} — {tr(key, fallback)}: {conflict.values.join(' / ')}
                    </li>
                  );
                })}
              </ul>
            </div>
          </COInlineMessage>
        )}

        {keptApart.length > 0 && (
          <COInlineMessage variant={'info'} dataTestId={'mergeKeptApart'}>
            <div className={'flex flex-col gap-1'}>
              <strong>{tr('CreateExpedition.mergeKeptApartHeading', 'Some stops at the same place were kept separate')}</strong>
              <ul className={'m-0 flex list-disc flex-col gap-1 pl-4'}>
                {keptApart.map(entry => (
                  <li key={`${entry.locationName}-${entry.reason}`}>
                    {entry.reason === 'timeslots'
                      ? tr('CreateExpedition.mergeKeptApartTimeslots', '{{location}} — the shipments need it at times that do not overlap', {
                          location: entry.locationName,
                        })
                      : tr('CreateExpedition.mergeKeptApartOrders', '{{location}} — combining them would leave orders unloaded before they are loaded', {
                          location: entry.locationName,
                        })}
                  </li>
                ))}
              </ul>
            </div>
          </COInlineMessage>
        )}
      </div>
    </Card>
  );
};

export default MergeInfoPanel;
